import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Capacitor } from "@capacitor/core";

import {
  apiRequest,
  getToken,
  removeToken,
  devError,
  SuccessToast,
  toastConfig,
  ErrorToast,
} from "@ridecloud/shared";

import {
  Car,
  MapPin,
  Users,
  Clock,
  TrendingUp,
  AlertTriangle,
  Search,
  Plus,
  Navigation,
  Zap,
  Menu,
  X,
  Bell,
  Settings,
  User,
  LogOut,
} from "lucide-react";

const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();

  const [advisoryText, setAdvisoryText] = useState(
    "No traffic advisories at this time."
  );
  const [activeRides, setActiveRides] = useState(12);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchTrafficAdvisory = async () => {
      try {
        const result = await apiRequest(async (APIv1) =>
          APIv1.get("/user/dashboard/traffic_advisory", {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          })
        );

        setAdvisoryText(
          result.data.content || "No traffic advisories at this time."
        );
      } catch (error) {
        ErrorToast("Failed to load traffic advisory");
        devError("Error fetching advisory:", error);
      }
    };

    //  verifyToken();
    fetchTrafficAdvisory();
  }, []);
  const handleLogout = async () => {
    await removeToken();
    onLogout?.();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50 to-green-100">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-lime-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Navigation Bar */}
      <nav
        className={`relative z-50 bg-white/90 backdrop-blur-xl border-b border-green-200/50 shadow-lg`}
        style={{
          paddingTop: Capacitor.isNativePlatform()
            ? "env(safe-area-inset-top)"
            : undefined,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            {/* Logo Section */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <img
                  src="/img/ride_cloud.png"
                  alt="icon"
                  className="w-10 h-10 sm:w-10 sm:h-10"
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                  RideCloud
                </h2>
                <p className="text-xs text-teal-600 font-medium">
                  Smart Carpooling
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-3">
              <div className="flex items-center space-x-6">
                {/* <a
                  href="#"
                  className="relative group px-3 py-2 text-gray-700 hover:text-green-600 font-medium transition-all duration-300"
                >
                  Dashboard
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></span>
                </a> */}
                <a
                  href="/my-rides"
                  className="flex items-center px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
                >
                  My Rides
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></span>
                </a>
                {/* <a
                  href="#"
                  className="relative group px-3 py-2 text-gray-700 hover:text-green-600 font-medium transition-all duration-300"
                >
                  Community
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></span>
                </a>
                <a
                  href="#"
                  className="relative group px-3 py-2 text-gray-700 hover:text-green-600 font-medium transition-all duration-300"
                >
                  History
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></span>
                </a> */}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                {/* <button className="relative p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all duration-300">
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                </button>
                <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all duration-300">
                  <Settings className="w-5 h-5" />
                </button> */}
                <Link
                  to="/profile"
                  className="flex items-center px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
                >
                  <User className="w-5 h-5 mr-2" />
                  Profile
                </Link>
                <button
                  onClick={handleLogout} // Use this for a logout function
                  // Alternatively, use <Link to="/logout"> if logout is a route
                  className="flex items-center gap-2 p-3 text-gray-700 hover:text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-xl transition-colors duration-200"
                  aria-label="Log out"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-3">
              {/* <button className="relative p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all duration-300">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button> */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all duration-300"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`lg:hidden transition-all duration-300 ease-in-out ${
              isMobileMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            } overflow-hidden`}
          >
            <div className="py-4 space-y-2 border-t border-green-200/50">
              {/* <a
                href="#"
                className="block px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
              >
                Dashboard
              </a> */}
              <a
                href="/my-rides"
                className="block px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
              >
                My Rides
              </a>
              {/* <a
                href="#"
                className="block px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
              >
                Community
              </a>
              <a
                href="#"
                className="block px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
              >
                History
              </a> */}
              <div className="border-t border-green-200/50 pt-3 mt-3">
                {/* <a
                  href="#"
                  className="flex items-center px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
                >
                  <Settings className="w-5 h-5 mr-3" />
                  Settings
                </a> */}
                <a
                  href="/profile"
                  className="flex items-center px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-xl font-medium transition-all duration-300"
                >
                  <User className="w-5 h-5 mr-3" />
                  Profile
                </a>
                <button
                  onClick={handleLogout} // Use this for a logout function
                  // Alternatively, use <Link to="/logout"> if logout is a route
                  className="flex items-center gap-2 p-3 text-gray-700 hover:text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-xl transition-colors duration-200"
                  aria-label="Log out"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        style={{
          paddingTop: Capacitor.isNativePlatform()
            ? "calc(env(safe-area-inset-top) + 1rem)"
            : undefined,
        }}
      >
        {/* Hero Section */}
        <section className="text-center mb-12">
          <div className="inline-flex items-center bg-green-100/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-green-200">
            <Zap className="w-4 h-4 text-green-600 mr-2" />
            <span className="text-sm text-gray-700">
              Welcome back! Ready for your next adventure?
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-4 leading-tight">
            Your Journey
            <span className="bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 bg-clip-text text-transparent">
              {" "}
              Awaits
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Connect with fellow travelers, save money, and reduce your carbon
            footprint with intelligent ride sharing.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/search-rides">
              <button className="group relative bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-teal-500/25 min-w-[200px]">
                <div className="flex items-center justify-center space-x-3">
                  <Search className="w-6 h-6" />
                  <span>Find a Ride</span>
                </div>
                <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </Link>

            <Link to="/Post-ride">
              <button className="group relative bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-teal-500/25 min-w-[200px]">
                <div className="flex items-center justify-center space-x-3">
                  <Plus className="w-6 h-6" />
                  <span>Offer a Ride</span>
                </div>
                <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </Link>
          </div>
        </section>

        {/* Traffic Advisory Section */}
        <section className="mb-8">
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 border border-green-200/50 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-12 sm:w-12 sm:h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <Navigation className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl text-base font-bold text-gray-800">
                  Live Traffic Intelligence
                </h2>
                <p className="text-sm sm:text-base text-gray-600">
                  Real-time updates for optimal route planning
                </p>
              </div>
            </div>

            <div
              className="
    bg-green-50/80 rounded-2xl p-4 border border-green-200/50
    w-full
    max-w-full
    lg:max-w-5xl
    xl:max-w-6xl
    mx-auto
  "
            >
              <div className="flex items-start space-x-2 mb-4 mt-1">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-gray-800 font-semibold mb-2">
                    Current Conditions
                  </h3>
                  <pre className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-wrap font-mono">
                    {advisoryText}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
